<ng-template #contents>
  <div class="pp-content-book__toc" data-testid="content-book-toc">
    @if (treeNodes().length === 0) {
      <p class="pp-content-book__hint">{{ 'base_document.content_book.widget.empty' | transloco }}</p>
    }
    <pp-tree-nav [nodes]="treeNodes()" [selectedId]="selectedNodeId()" [expandAll]="true" (selected)="select($event.id)">
      @if (canEdit()) {
        <ng-template ppTreeNavNodeActions let-node>
          <button
            mat-icon-button
            type="button"
            class="pp-content-book__node-menu"
            [attr.data-testid]="'content-book-node-actions-' + node.id"
            [attr.aria-label]="'base_document.content_book.widget.actions' | transloco"
            [matMenuTriggerFor]="nodeMenu"
            [matMenuTriggerData]="{ node }"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </ng-template>
      }
    </pp-tree-nav>
  </div>
</ng-template>

<mat-menu #nodeMenu>
  <ng-template matMenuContent let-node="node">
    <button mat-menu-item type="button" data-testid="content-book-add-child" (click)="addDocument('child', node.id)">
      <mat-icon>subdirectory_arrow_right</mat-icon>{{ 'base_document.content_book.widget.add_child' | transloco }}
    </button>
    <mat-divider />
    <button mat-menu-item type="button" data-testid="content-book-move-up" [disabled]="!can('up', node.id)" (click)="moveNode(node.id, 'up')">
      <mat-icon>arrow_upward</mat-icon>{{ 'base_document.content_book.widget.move_up' | transloco }}
    </button>
    <button mat-menu-item type="button" data-testid="content-book-move-down" [disabled]="!can('down', node.id)" (click)="moveNode(node.id, 'down')">
      <mat-icon>arrow_downward</mat-icon>{{ 'base_document.content_book.widget.move_down' | transloco }}
    </button>
    <button mat-menu-item type="button" data-testid="content-book-indent" [disabled]="!can('indent', node.id)" (click)="indentNode(node.id)">
      <mat-icon>format_indent_increase</mat-icon>{{ 'base_document.content_book.widget.indent' | transloco }}
    </button>
    <button mat-menu-item type="button" data-testid="content-book-outdent" [disabled]="!can('outdent', node.id)" (click)="outdentNode(node.id)">
      <mat-icon>format_indent_decrease</mat-icon>{{ 'base_document.content_book.widget.outdent' | transloco }}
    </button>
    <mat-divider />
    <button mat-menu-item type="button" data-testid="content-book-remove" (click)="removeNode(node.id)">
      <mat-icon>playlist_remove</mat-icon>{{ 'base_document.content_book.widget.remove' | transloco }}
    </button>
    <button mat-menu-item type="button" data-testid="content-book-delete-document" (click)="deleteDocument(node.id)">
      <mat-icon>delete</mat-icon>{{ 'base_document.content_book.widget.delete_document' | transloco }}
    </button>
  </ng-template>
</mat-menu>

<mat-menu #addMenu>
  <button mat-menu-item type="button" data-testid="content-book-add-below" [disabled]="!selectedNode()" (click)="addDocument('child')">
    <mat-icon>subdirectory_arrow_right</mat-icon>{{ 'base_document.content_book.widget.add_child' | transloco }}
  </button>
  <mat-divider />
  <button mat-menu-item type="button" data-testid="content-book-add-root" (click)="addDocument('root')">
    <mat-icon>add</mat-icon>{{ 'base_document.content_book.widget.add_root' | transloco }}
  </button>
</mat-menu>

@if (loading()) {
  <p class="pp-content-book__hint">{{ 'base_document.content_book.widget.loading' | transloco }}</p>
} @else if (loadError(); as error) {
  <p role="alert" class="pp-content-book__error" data-testid="content-book-error">{{ error }}</p>
} @else if (book()) {
  @if (!inSidenav() || canEdit()) {
    <div class="pp-content-book__toolbar" role="toolbar" data-testid="content-book-toolbar">
      @if (!inSidenav()) {
        <h3 class="pp-content-book__title">{{ book()?.title }}</h3>
      }
      <span class="pp-content-book__spacer"></span>
      @if (canEdit()) {
        @if (pageState(); as state) {
          <span class="pp-content-book__status" [class]="'pp-content-book__status--' + state.kind" data-testid="content-book-status">{{ state.label }}</span>
        }
        <mat-slide-toggle data-testid="content-book-show-unpublished" [checked]="showUnpublished()" (change)="showUnpublished.set($event.checked)">
          {{ 'base_document.content_book.widget.show_unpublished' | transloco }}
        </mat-slide-toggle>
        <button mat-button type="button" data-testid="content-book-add-document" [matMenuTriggerFor]="addMenu">
          <mat-icon>add</mat-icon>{{ 'base_document.content_book.widget.add' | transloco }}
        </button>
        <button
          mat-icon-button
          type="button"
          data-testid="content-book-add-before"
          [disabled]="!selectedNode() || busy()"
          [matTooltip]="'base_document.content_book.widget.add_before' | transloco"
          [attr.aria-label]="'base_document.content_book.widget.add_before' | transloco"
          (click)="addDocument('before')"
        >
          <mat-icon>vertical_align_top</mat-icon>
        </button>
        <button
          mat-icon-button
          type="button"
          data-testid="content-book-add-after"
          [disabled]="!selectedNode() || busy()"
          [matTooltip]="'base_document.content_book.widget.add_after' | transloco"
          [attr.aria-label]="'base_document.content_book.widget.add_after' | transloco"
          (click)="addDocument('after')"
        >
          <mat-icon>vertical_align_bottom</mat-icon>
        </button>
        <button mat-button type="button" data-testid="content-book-page-edit" [disabled]="!pageView()?.canEdit()" (click)="pageView()?.toggleEditing()">
          <mat-icon>{{ pageView()?.editing() ? 'visibility' : 'edit' }}</mat-icon>
          {{ (pageView()?.editing() ? 'base_document.content_book.widget.read' : 'base_document.content_book.widget.edit') | transloco }}
        </button>
        <button
          mat-icon-button
          type="button"
          data-testid="content-book-delete"
          [disabled]="!selectedNode() || busy()"
          [matTooltip]="'base_document.content_book.widget.delete_document' | transloco"
          [attr.aria-label]="'base_document.content_book.widget.delete_document' | transloco"
          (click)="deleteDocument(selectedNodeId()!)"
        >
          <mat-icon>delete</mat-icon>
        </button>
        <button
          mat-icon-button
          type="button"
          data-testid="content-book-pdf"
          [disabled]="!selectedDocument() || pageView()?.editing() || busy()"
          [matTooltip]="'base_document.content_book.widget.pdf_report' | transloco"
          [attr.aria-label]="'base_document.content_book.widget.pdf_report' | transloco"
          (click)="exportPdf()"
        >
          <mat-icon>picture_as_pdf</mat-icon>
        </button>
        <button mat-button type="button" data-testid="content-book-publish" [disabled]="!canPublishPage() || busy()" (click)="publishPage()">
          <mat-icon>publish</mat-icon>{{ 'base_document.content_book.widget.publish' | transloco }}
        </button>
        <button mat-button type="button" data-testid="content-book-archive" [disabled]="!canArchivePage() || busy()" (click)="archivePage()">
          <mat-icon>archive</mat-icon>{{ 'base_document.content_book.widget.archive' | transloco }}
        </button>
        <button
          mat-icon-button
          type="button"
          data-testid="content-book-properties"
          [disabled]="!propertiesUrl()"
          [matTooltip]="'base_document.content_book.widget.properties' | transloco"
          [attr.aria-label]="'base_document.content_book.widget.properties' | transloco"
          (click)="openProperties()"
        >
          <mat-icon>tune</mat-icon>
        </button>
      }
    </div>
  }
  @if (actionError(); as error) {
    <p role="alert" class="pp-content-book__error" data-testid="content-book-action-error">{{ error }}</p>
  }
  <div class="pp-content-book" [class.pp-content-book--inline]="!inSidenav()">
    @if (!inSidenav()) {
      <aside class="pp-content-book__aside">
        <ng-container *ngTemplateOutlet="contents" />
      </aside>
    }
    <article class="pp-content-book__page" data-testid="content-book-page">
      @if (selectedNode(); as node) {
        <pp-content-book-document-view [documentId]="node.documentId" [locale]="effectiveLocale()" [document]="documents().get(node.documentId)" />
      }
    </article>
  </div>
}
